import { ModuleLabel } from '../module-label';
import { useContext } from 'react';
import { bookmarkEventKey, useScopedBookmarkSelection } from '../../general/bookmark-selection';
import { Dropdown } from 'primereact/dropdown';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Message } from 'primereact/message';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { FormFieldOption, FormFieldType, QuizResultEventDto, SavedQuizAnswersDto, UserDto, UserQuizFilter } from '../../general/dtos';
import { hasExpectedAnswer, isMultiChoiceType } from '../content-viewer/multi-choice-fields';
import { UserManagementDataContext } from '../../providers/user-management-provider';
import { useUserQuizOptions, useUserQuizResults } from '../../providers/user-quiz-results-provider';

interface QuizAnswer {
    field?: { key?: string; label?: string; type?: FormFieldType; options?: FormFieldOption[] };
    userAnswer?: unknown;
    expectedAnswer?: unknown;
    isCorrect?: boolean | null;
}

interface QuizRecord {
    key: string;
    occured_at: string;
    module_name: string;
    started_at: string;
    event?: QuizResultEventDto;
    saved?: SavedQuizAnswersDto;
}

function formatQuizTimestamp(value: string): string {
    return value.replace(/(\d{2}:\d{2}:\d{2})\.\d+/, '$1').replace('T', ' ').replace(/Z$/, ' UTC');
}

function readPayload(event: QuizResultEventDto) {
    try {
        const payload = JSON.parse(event.payload);
        return { text: JSON.stringify(payload, null, 2), results: (Array.isArray(payload?.results) ? payload.results : []) as QuizAnswer[] };
    } catch {
        return { text: event.payload, results: [] as QuizAnswer[] };
    }
}

function displayAnswer(answer: unknown, field?: QuizAnswer['field']): string {
    if (Array.isArray(answer) && field?.type && isMultiChoiceType(field.type)) {
        return answer.map(key => field.options?.find(option => option.key === key)?.value ?? key).join(', ') || 'None';
    }
    if (typeof answer === 'boolean') return answer ? 'Yes' : 'No';
    if (field?.type === 'select' || field?.type === 'radio')
        return field.options?.find(option => option.key === String(answer) || option.value === String(answer))?.value ?? String(answer ?? '');
    if (field?.type === 'quiz-list' && answer && typeof answer === 'object' && !Array.isArray(answer)) {
        const label = (key: unknown) => field.options?.find(option => option.key === String(key))?.value ?? String(key ?? '');
        return Object.entries(answer).map(([target, value]) => `${label(target)}: ${label(value) || 'No answer'}`).join('; ');
    }
    return answer === null || answer === undefined ? '' : typeof answer === 'string' ? answer : JSON.stringify(answer);
}

export function UserQuizResults({ selectedUsers }: { selectedUsers: UserDto[] }) {
    if (selectedUsers.length !== 1) return <Message severity="info" text="Select one user to view quiz results." />;
    // Bookmarked filters are scoped to a user so another user's selections cannot carry over.
    return <SelectedUserQuizResults key={selectedUsers[0].sub} sub={selectedUsers[0].sub} />;
}

function SelectedUserQuizResults({ sub }: { sub: string }) {
    const ctx = useContext(UserManagementDataContext)!;
    const [assignment, setAssignment] = useScopedBookmarkSelection('quiz', sub);
    const [attempt, setAttempt] = useScopedBookmarkSelection('attempt', `${sub}:${assignment ?? ''}`);
    const filter: UserQuizFilter | null = !assignment ? null : assignment.startsWith('course:')
        ? { course_key: assignment.slice(7) } : { module_key: assignment.slice(7) };
    const { events, savedAnswers, loading, error } = useUserQuizResults(sub, filter);
    const records: QuizRecord[] = [
        ...savedAnswers.map(saved => ({ key: `saved:${saved.module_key}:${saved.fragment_key}`, occured_at: saved.updated_at,
            module_name: saved.module_name, started_at: '', saved })),
        ...events.map(event => ({ key: bookmarkEventKey(event), occured_at: event.occured_at,
            module_name: event.module_name, started_at: event.started_at, event })),
    ];
    const selectedRecord = records.find(record => record.key === attempt) ?? null;
    const selectedEvent = selectedRecord?.event ?? null;
    const { options: available, loading: optionsLoading, error: optionsError } = useUserQuizOptions(sub);
    const courseKeys = new Set(available.filter(o => o.course_key !== null).map(o => o.course_key));
    const moduleKeys = new Set(available.filter(o => o.course_key === null).map(o => o.module_key));
    const showModuleColumn = !!filter?.course_key && new Set(available
        .filter(option => option.course_key === filter.course_key).map(option => option.module_key)).size > 1;
    const options = [
        { label: 'Courses', items: ctx.allCourses.filter(c => courseKeys.has(c.course_key))
            .map(c => ({ label: c.name, value: `course:${c.course_key}` })) },
        { label: 'Individual managed modules', items: ctx.allModules
            .filter(m => moduleKeys.has(m.module_key))
            .map(m => ({ label: m.name, value: `module:${m.module_key}`, managed: m.is_managed })) }
    ].filter(group => group.items.length > 0);
    const payload = selectedEvent && readPayload(selectedEvent);
    const scoredAnswers = payload?.results.filter(r => hasExpectedAnswer(r.expectedAnswer, r.field?.type)) ?? [];
    const correctAnswers = scoredAnswers.filter(r => r.isCorrect === true).length;
    const percentage = scoredAnswers.length === 0 ? 0 : Math.round(correctAnswers / scoredAnswers.length * 100);

    return <div className="flex flex-column h-full gap-3" style={{ minHeight: 0 }}>
        <div>
            <label htmlFor="quiz-assignment" className="block mb-2">Course or individual managed module</label>
            <Dropdown inputId="quiz-assignment" value={assignment} options={options}
                optionGroupLabel="label" optionGroupChildren="items" optionLabel="label" optionValue="value"
                itemTemplate={option => <ModuleLabel name={option.label} managed={option.managed} />}
                valueTemplate={(option, props) => option ? <ModuleLabel name={option.label} managed={option.managed} /> : props.placeholder}
                placeholder="Select a course or module" filter showClear
                loading={optionsLoading || ctx.allCoursesLoading || ctx.allModulesLoading}
                emptyMessage="No quiz results available for this user."
                className="w-full" onChange={e => { setAttempt(null); setAssignment(e.value ?? null); }} />
        </div>
        {(error || optionsError || ctx.allCoursesError || ctx.allModulesError) &&
            <Message severity="error" text={error ?? optionsError ?? ctx.allCoursesError ?? ctx.allModulesError ?? ''} />}
        <div className="grid flex-1" style={{ minHeight: 0, overflow: 'auto' }}>
            <div className="col-12 lg:col-5">
                <DataTable value={records} loading={loading} size="small" selectionMode="single"
                    compareSelectionBy="equals"
                    selection={selectedRecord} onSelectionChange={e => setAttempt((e.value as QuizRecord | null)?.key ?? null)}
                    scrollable scrollHeight="flex"
                    emptyMessage={assignment ? 'No saved final quiz answers or verification attempts recorded.' : 'Select a course or module to view quiz answers and attempts.'}>
                    <Column header={<span className="sr-only">Record type</span>} style={{ width: '3rem' }} bodyStyle={{ textAlign: 'center' }}
                        body={(record: QuizRecord) => <span className="text-color-secondary" role="img" tabIndex={0}
                            aria-label={record.saved ? 'Saved answers' : 'Verification attempt'}
                            title={record.saved ? 'Latest saved answers' : `Verification attempt. Session started: ${formatQuizTimestamp(record.started_at)}`}>
                            <i className={`pi ${record.saved ? 'pi-file-edit' : 'pi-check-square'}`} aria-hidden="true" />
                        </span>} />
                    <Column field="occured_at" header="Recorded" body={(record: QuizRecord) =>
                        <span className="white-space-nowrap">{formatQuizTimestamp(record.occured_at)}</span>} />
                    {showModuleColumn && <Column field="module_name" header="Module" body={(record: QuizRecord) => <ModuleLabel name={record.module_name} managed />} />}
                </DataTable>
            </div>
            <div className="col-12 lg:col-7">
                {selectedRecord?.saved ? <>
                    <h3 className="mt-0">Saved quiz answers</h3>
                    <p><strong><ModuleLabel name={selectedRecord.module_name} managed /></strong> · {selectedRecord.saved.fragment_title}</p>
                    <p>Last saved: {formatQuizTimestamp(selectedRecord.saved.updated_at)}</p>
                    <p>These are the latest saved answers. This record has no verification score.</p>
                    <DataTable value={selectedRecord.saved.answers} size="small">
                        <Column header="Question" body={(r: QuizAnswer) => r.field?.label || r.field?.key || ''} />
                        <Column header="User answer" bodyStyle={{ color: 'var(--text-color-secondary)' }} body={(r: QuizAnswer) => displayAnswer(r.userAnswer, r.field)} />
                    </DataTable>
                </> : selectedEvent && payload ? <>
                    <h3 className="mt-0">Quiz event details</h3>
                    <dl className="align-items-baseline gap-2" style={{ display: 'grid', gridTemplateColumns: '7rem minmax(0, 1fr)' }}>
                        <dt>Module</dt><dd className="m-0 font-bold"><ModuleLabel name={selectedEvent.module_name} managed /></dd>
                        <dt>Submitted</dt><dd className="m-0 font-bold">{formatQuizTimestamp(selectedEvent.occured_at)}</dd>
                    </dl>
                    {payload.results.length > 0 && <><DataTable value={payload.results} size="small">
                        <Column header="Question" body={(r: QuizAnswer) => r.field?.label ?? r.field?.key ?? ''} />
                        <Column header="User answer" bodyStyle={{ color: 'var(--text-color-secondary)' }} body={(r: QuizAnswer) => displayAnswer(r.userAnswer, r.field)} />
                        <Column header="Expected answer" bodyStyle={{ color: 'var(--text-color-secondary)' }} body={(r: QuizAnswer) => displayAnswer(r.expectedAnswer, r.field)} />
                        <Column header="Result" body={(r: QuizAnswer) => r.isCorrect === true ? 'Correct' : r.isCorrect === false ? 'Incorrect' : 'Not checked'} />
                    </DataTable>
                    <p className="font-semibold mt-3">Correct answers {correctAnswers}/{scoredAnswers.length} — {percentage}%</p>
                    </>}
                    <h4>Event payload</h4>
                    <SyntaxHighlighter language="json" style={oneDark} wrapLongLines
                        className="border-1 surface-border border-round" customStyle={{ margin: 0, overflowWrap: 'anywhere' }}>
                        {payload.text}
                    </SyntaxHighlighter>
                </> : <Message severity="info" text="Select saved answers or a verification attempt to view its details." />}
            </div>
        </div>
    </div>;
}
